import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getLeads, getLeadStats, getMetaPages, reactivateLeads } from '@/services/api';
import {
    LeadStats,
    LeadFiltersComponent,
    LeadTable,
    ViewLeadModal,
    SyncLeadsDialog,
    FloatingSyncStatus,
} from './components';
import { Lead, LeadFilters, LeadResponse, LeadTag } from '@/types/meta-lead.ts';
import { Button } from '@/components/ui/button.tsx';
import { ChevronLeft, ChevronRight, RefreshCw, CheckCircle, X, Users } from 'lucide-react';

type PageOption = { id: string; name: string };

export default function Leads() {
    const queryClient = useQueryClient();
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false);
    const [activeSyncTaskId, setActiveSyncTaskId] = useState<string | null>(null);
    const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [filters, setFilters] = useState<LeadFilters>({
        sortBy: 'last_interaction_at',
        sortOrder: 'desc',
        in_funnel: 'all',
        in_24h_window: 'all',
        is_active: 'all',
    });

    const perPage = 20;

    // Query para páginas do Meta
    const { data: metaPagesData } = useQuery({
        queryKey: ['meta-pages'],
        queryFn: getMetaPages,
        staleTime: 1000 * 60,
    });

    // Query para estatísticas (com filtros)
    const { data: statsData, isPlaceholderData: isStatsStale } = useQuery({
        queryKey: ['lead-stats', filters],
        queryFn: () => getLeadStats({
            page_id: filters.page,
            tag_id: filters.tag,
            in_funnel: filters.in_funnel === 'all' ? undefined : filters.in_funnel === 'yes',
            in_24h_window: filters.in_24h_window === 'all' ? undefined : filters.in_24h_window === 'yes',
            is_active: filters.is_active === 'all' ? undefined : filters.is_active === 'yes',
            search: filters.search,
            project_ids: filters.project_ids,
        }),
        placeholderData: (prev) => prev,
        staleTime: 1000 * 30,
        refetchInterval: 1000 * 30,
    });

    // Query para listar leads
    const { data: leadsData, isLoading, isFetching, error, isPlaceholderData: isLeadsStale } = useQuery({
        queryKey: ['leads', currentPage, filters],
        queryFn: () => getLeads({
            page: currentPage,
            per_page: perPage,
            page_id: filters.page,
            tag_id: filters.tag,
            in_funnel: filters.in_funnel === 'all' ? undefined : filters.in_funnel === 'yes',
            in_24h_window: filters.in_24h_window === 'all' ? undefined : filters.in_24h_window === 'yes',
            is_active: filters.is_active === 'all' ? undefined : filters.is_active === 'yes',
            search: filters.search,
            project_ids: filters.project_ids,
            // Backend sabe lidar com `total_clicks` (path especial via
            // ClickHouse — vide LeadService.list_leads).
            sort_by: (filters.sortBy as any) || 'last_interaction_at',
            sort_order: filters.sortOrder || 'desc',
        }),
        placeholderData: (prev) => prev,
        staleTime: 1000 * 15,
        refetchInterval: 1000 * 20,
    });

    // isLoading = true só no primeiro carregamento (sem dados anteriores)
    // isFetching = true sempre que está buscando (inclusive com dados anteriores)
    const isFirstLoad = isLoading && !leadsData;

    const leads = useMemo((): Lead[] => {
        if (!leadsData?.items) return [];

        return leadsData.items.map((response: LeadResponse) => ({
            id: response.id,
            user_id: response.user_id,
            sender_id: response.sender_id,
            page_id: response.page_id,
            page_name: response.page_name,
            nome: response.nome,
            sobrenome: response.sobrenome,
            pais: response.pais,
            cidade: response.cidade,
            estado: response.estado,
            sexo: response.sexo,
            is_active: response.is_active,
            last_error_code: response.last_error_code,
            last_interaction_at: response.last_interaction_at,
            in_24h_window: response.in_24h_window,
            current_funnel_id: response.current_funnel_id,
            last_node_id: response.last_node_id,
            tags: response.tags,
            total_clicks: response.total_clicks,
            created_at: response.created_at,
            updated_at: response.updated_at
        }));
    }, [leadsData]);

    // Converter páginas do Meta para PageOption
    const pages = useMemo<PageOption[]>(() => {
        if (!metaPagesData?.length) return [];

        return metaPagesData
            .map(page => ({
                id: page.page_id,
                name: page.page_name
            }))
            .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    }, [metaPagesData]);

    // Extrair tags únicas dos leads
    const allTags = useMemo<LeadTag[]>(() => {
        if (!leadsData?.items?.length) return [];

        const unique = new Map<number, LeadTag>();

        for (const lead of leadsData.items) {
            for (const tag of lead.tags || []) {
                if (!unique.has(tag.id)) {
                    unique.set(tag.id, tag);
                }
            }
        }

        return Array.from(unique.values()).sort((a, b) =>
            a.nome.localeCompare(b.nome, 'pt-BR')
        );
    }, [leadsData]);

    // Mutation para reativar leads
    const reactivateMutation = useMutation({
        mutationFn: (leadIds: number[]) => reactivateLeads(leadIds),
        onSuccess: () => {
            setSelectedIds(new Set());
            queryClient.invalidateQueries({ queryKey: ['leads'] });
            queryClient.invalidateQueries({ queryKey: ['lead-stats'] });
        },
    });

    // Selection handlers
    const handleToggleSelect = (leadId: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(leadId)) {
                next.delete(leadId);
            } else {
                next.add(leadId);
            }
            return next;
        });
    };

    const handleClearSelection = () => {
        setSelectedIds(new Set());
    };

    const handleReactivate = () => {
        if (selectedIds.size === 0) return;
        reactivateMutation.mutate(Array.from(selectedIds));
    };

    // Handlers
    const handleView = (lead: Lead) => {
        setSelectedLeadId(lead.id);
        setIsViewModalOpen(true);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const totalPages = leadsData?.pages || 1;

    const handleSyncComplete = () => {
        queryClient.invalidateQueries({ queryKey: ['leads'] });
        queryClient.invalidateQueries({ queryKey: ['lead-stats'] });
    };

    const handleBackgroundSync = (taskId: string) => {
        setActiveSyncTaskId(taskId);
        setIsSyncDialogOpen(false);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#0f172a] via-[#1e293b] to-[#0f172a] p-8 shadow-xl">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#7dd3fc]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#38bdf8]/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />

                <div className="relative flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10">
                            <Users className="w-6 h-6 text-[#7dd3fc]" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-white">Leads</h1>
                            <p className="mt-1 text-sm text-slate-300">
                                Gerencie e acompanhe seus contatos
                            </p>
                        </div>
                    </div>
                    <Button
                        onClick={() => setIsSyncDialogOpen(true)}
                        className="bg-white/10 hover:bg-white/20 text-white font-medium px-6 py-2.5 rounded-xl border border-white/10 backdrop-blur-sm transition-all duration-300 hover:shadow-lg hover:shadow-[#7dd3fc]/10"
                    >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Sincronizar Leads
                    </Button>
                </div>
            </div>

            {/* Loading State - só no primeiro carregamento */}
            {isFirstLoad && (
                <div className="rounded-2xl bg-white p-8 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0f172a]"></div>
                        <p className="ml-3 text-gray-500">Carregando leads...</p>
                    </div>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className="rounded-2xl bg-red-50 border border-red-200/50 p-6">
                    <p className="text-red-700 font-medium">
                        Erro ao carregar leads. Tente novamente.
                    </p>
                </div>
            )}

            {/* Stats */}
            {!isFirstLoad && !error && statsData && (
                <LeadStats
                    totalLeads={statsData.total_leads}
                    activeLeads={statsData.active_leads}
                    leadsInFunnel={statsData.leads_in_funnel}
                    newToday={statsData.new_today ?? 0}
                    isUpdating={isStatsStale}
                />
            )}

            {/* Filtros */}
            {!isFirstLoad && !error && (statsData || pages.length > 0) && (
                <LeadFiltersComponent
                    pages={pages}
                    tags={allTags}
                    filters={filters}
                    onFiltersChange={(newFilters) => {
                        setFilters(newFilters);
                        setCurrentPage(1);
                    }}
                />
            )}

            {/* Barra de ações em lote */}
            {selectedIds.size > 0 && (
                <div className="flex items-center justify-between bg-gradient-to-r from-[#0f172a] to-[#1e293b] text-white rounded-2xl p-4 shadow-lg">
                    <span className="text-sm font-medium">
                        {selectedIds.size} lead{selectedIds.size > 1 ? 's' : ''} selecionado{selectedIds.size > 1 ? 's' : ''}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={handleReactivate}
                            disabled={reactivateMutation.isPending}
                            className="rounded-xl"
                        >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            {reactivateMutation.isPending ? 'Reativando...' : 'Visar Novamente'}
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            className="text-white hover:text-gray-200 hover:bg-white/10 rounded-xl"
                            onClick={handleClearSelection}
                        >
                            <X className="w-4 h-4 mr-1" />
                            Limpar
                        </Button>
                    </div>
                </div>
            )}

            {/* Lista de Leads */}
            {!isFirstLoad && !error && leads.length > 0 ? (
                <>
                    <div className={`transition-opacity duration-300 ${isLeadsStale ? 'opacity-50' : 'opacity-100'}`}>
                        <LeadTable
                            leads={leads}
                            onView={handleView}
                            selectedIds={selectedIds}
                            onToggleSelect={handleToggleSelect}
                        />
                    </div>

                    {/* Paginação */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                            <div className="text-sm text-gray-500">
                                Página {currentPage} de {totalPages} ({leadsData?.total || 0} leads no total)
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="rounded-xl"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    Anterior
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="rounded-xl"
                                >
                                    Próxima
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            ) : !isFirstLoad && !error && leads.length === 0 && !isFetching && (currentPage > 1 || Object.keys(filters).length > 2) ? (
                <div className="rounded-2xl bg-white p-12 shadow-sm border border-gray-100 text-center">
                    <div className="mx-auto w-16 h-16 bg-gradient-to-br from-[#7dd3fc]/20 to-[#38bdf8]/10 rounded-2xl flex items-center justify-center mb-4">
                        <svg
                            className="w-8 h-8 text-[#0f172a]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                            />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Nenhum lead encontrado
                    </h3>
                    <p className="text-gray-600 mb-6">
                        Tente ajustar os filtros para ver mais resultados.
                    </p>
                </div>
            ) : null}

            {/* Empty State */}
            {!isFirstLoad && !error && leads.length === 0 && !isFetching && currentPage === 1 && Object.keys(filters).length === 2 && (
                <div className="rounded-2xl bg-white p-12 shadow-sm border border-gray-100 text-center">
                    <div className="mx-auto w-16 h-16 bg-gradient-to-br from-[#7dd3fc]/20 to-[#38bdf8]/10 rounded-2xl flex items-center justify-center mb-4">
                        <svg
                            className="w-8 h-8 text-[#0f172a]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                            />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Nenhum lead cadastrado
                    </h3>
                    <p className="text-gray-600 mb-6">
                        Seus leads aparecerão aqui assim que começarem a interagir.
                    </p>
                </div>
            )}

            {/* Modal de Visualização */}
            <ViewLeadModal
                open={isViewModalOpen}
                onOpenChange={setIsViewModalOpen}
                leadId={selectedLeadId}
            />

            {/* Dialog de Sincronização */}
            <SyncLeadsDialog
                open={isSyncDialogOpen}
                onOpenChange={setIsSyncDialogOpen}
                pages={metaPagesData || []}
                onSyncComplete={handleSyncComplete}
                onBackgroundSync={handleBackgroundSync}
            />

            {activeSyncTaskId && (
                <FloatingSyncStatus
                    taskId={activeSyncTaskId}
                    onClose={() => setActiveSyncTaskId(null)}
                />
            )}
        </div>
    );
}
